'use client';

import { LifeBuoy } from 'lucide-react';
import { useAuthStore } from '@/features/auth/stores/auth-store';
import { useFabHidden } from '../lib/use-fab-hidden';
import { useBadgeCounts } from '@/features/notifications/hooks/use-notifications';
import { useSupportOpenCount } from '../hooks/use-support';

interface SidebarSupportButtonProps {
  collapsed?: boolean;
}

/**
 * Buton mic in footer-ul sidebar-ului care inlocuieste FAB-ul cand
 * userul l-a ascuns. Click → emite event 'sf-support:open' pe care
 * SupportWidget asculta si deschide panel-ul.
 *
 * Returneaza null daca FAB-ul e vizibil (niciodata vizibile in paralel).
 */
export function SidebarSupportButton({ collapsed = false }: SidebarSupportButtonProps) {
  const { hidden } = useFabHidden();
  const user = useAuthStore((s) => s.user);
  const isAdmin = user?.isSuperAdmin === true;

  // Aceeasi logica de badge ca FAB — admin vede unread conversations,
  // regular vede tichete proprii deschise.
  const { data: badgeData } = useBadgeCounts();
  const adminUnread = badgeData?.supportConversations ?? 0;
  const { data: openCount } = useSupportOpenCount();
  const badge = isAdmin ? adminUnread : (openCount?.count ?? 0);

  if (!hidden) return null;

  function handleClick() {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('sf-support:open'));
    }
  }

  if (collapsed) {
    return (
      <div className="border-t border-neutral-100 px-2 py-2 dark:border-neutral-800">
        <button
          type="button"
          onClick={handleClick}
          title="Suport"
          aria-label="Suport"
          className="relative flex w-full items-center justify-center rounded-lg px-2 py-2 text-neutral-500 transition-all hover:bg-neutral-50 hover:text-neutral-900 dark:text-neutral-400 dark:hover:bg-neutral-800 dark:hover:text-neutral-100"
        >
          <LifeBuoy className="h-5 w-5" strokeWidth={1.75} />
          {badge > 0 && (
            <span
              className={`absolute right-1 top-1 inline-block h-2 w-2 rounded-full ring-2 ring-white dark:ring-neutral-900 ${
                isAdmin ? 'bg-red-500 animate-pulse' : 'bg-amber-500'
              }`}
            />
          )}
        </button>
      </div>
    );
  }

  return (
    <div className="border-t border-neutral-100 px-2 py-2 dark:border-neutral-800">
      <button
        type="button"
        onClick={handleClick}
        className="group relative flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-sm font-medium text-neutral-500 transition-all hover:bg-neutral-50 hover:text-neutral-900 dark:text-neutral-400 dark:hover:bg-neutral-800 dark:hover:text-neutral-100"
      >
        <span className="relative inline-flex">
          <LifeBuoy className="h-5 w-5 flex-shrink-0" strokeWidth={1.75} />
          {badge > 0 && (
            <span
              className={`absolute -right-1 -top-1 inline-block h-2 w-2 rounded-full ring-2 ring-white dark:ring-neutral-900 ${
                isAdmin ? 'bg-red-500 animate-pulse' : 'bg-amber-500'
              }`}
            />
          )}
        </span>
        <span className="flex-1 text-left">Suport</span>
        {badge > 0 && (
          <span className="font-[JetBrains_Mono,ui-monospace,monospace] text-[0.625rem] tabular-nums text-amber-700 dark:text-amber-400">
            {badge > 99 ? '99+' : badge}
          </span>
        )}
      </button>
    </div>
  );
}
